import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Logo } from '../ui/Logo';
import { CheckCircle, Loader2, X, FileText, ShieldCheck, Heart } from 'lucide-react';
import { authApi } from '../../services/apiService';
import { UserProfile } from '../../types';
// Lazy load LegalContent - only needed when user opens terms modal
const LegalContent = lazy(() => import('../ui/LegalContent').then(m => ({ default: m.LegalContent })));
// Inline the constant to avoid bundling LegalContent.tsx
const LEGAL_LAST_UPDATED = 'January 2026';

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = '375860765903-a2s2tpnki5ljiqei1otkium9ogsdtj7s.apps.googleusercontent.com';

interface AuthResult {
  success: boolean;
  isNewUser: boolean;
  user: UserProfile;
}

interface AuthProps {
  onLogin: (result: AuthResult) => void;
}

// Extend window for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

export const Auth: React.FC<AuthProps> = ({ onLogin }) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [legalTab, setLegalTab] = useState<'terms' | 'privacy'>('terms');

  // Lazy load the Google Identity Services script
  useEffect(() => {
    const loadGoogleScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Check if already loaded
        if (window.google?.accounts?.id) {
          resolve();
          return;
        }

        // Check if script is already in DOM
        if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
          // Script exists but not loaded yet, wait for it
          const checkLoaded = setInterval(() => {
            if (window.google?.accounts?.id) {
              clearInterval(checkLoaded);
              resolve();
            }
          }, 50);
          return;
        }

        // Create and append the script
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          // Wait for the google object to be fully initialized
          const checkInit = setInterval(() => {
            if (window.google?.accounts?.id) {
              clearInterval(checkInit);
              resolve();
            }
          }, 50);
        };
        script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
        document.head.appendChild(script);
      });
    };

    loadGoogleScript()
      .then(() => setScriptLoaded(true))
      .catch((error) => {
        console.error('Failed to load Google Sign-In script:', error);
        setStatus('error');
        setErrorMessage('Failed to load sign-in. Please refresh the page.');
      });
  }, []);

  useEffect(() => {
    if (!scriptLoaded) return;

    const initGoogleSignIn = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
          auto_select: false, // Disabled auto-select until terms accepted
          context: 'signin',
          cancel_on_tap_outside: true,
          itp_support: true,
          use_fedcm_for_prompt: true,
        });
      }
    };

    initGoogleSignIn();
  }, [scriptLoaded]);

  // Render button only when terms are accepted
  useEffect(() => {
    if (!scriptLoaded || !termsAccepted) return;

    const renderButton = () => {
      const buttonElement = document.getElementById('google-signin-button');
      if (buttonElement && window.google?.accounts?.id) {
        // Clear existing content first
        buttonElement.innerHTML = '';
        window.google.accounts.id.renderButton(buttonElement, {
          theme: 'filled_black',
          size: 'large',
          width: 300,
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'center',
        });
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(renderButton, 50);
  }, [scriptLoaded, termsAccepted]);

  const handleGoogleCallback = async (response: { credential: string }) => {
    setStatus('loading');
    setErrorMessage('');

    try {
      const result = await authApi.verifyGoogle(response.credential);

      if (result.success) {
        setStatus('success');
        // Pass full result so App can check if user is onboarded
        onLogin({
          success: true,
          isNewUser: result.isNewUser,
          user: result.user as UserProfile,
        });
      } else {
        throw new Error('Verification failed');
      }
    } catch (error) {
      console.error('Google auth error:', error);
      setStatus('error');
      setErrorMessage('Sign in failed. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-8 bg-gradient-to-br from-stone-50 via-white to-brand-50">
        <div className="relative">
          <div className="absolute inset-0 bg-brand-400/20 rounded-full blur-2xl animate-pulse" />
          <div className="relative w-24 h-24 bg-gradient-to-br from-brand-400 to-brand-600 text-white rounded-full flex items-center justify-center shadow-xl shadow-brand-200">
            <CheckCircle className="w-12 h-12" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-stone-900 mt-8 mb-3">Welcome!</h2>
        <p className="text-stone-600 text-lg">Setting up your account...</p>
        <div className="flex gap-1.5 mt-6">
          <div className="w-2 h-2 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col bg-gradient-to-br from-rose-50 via-stone-50 to-brand-100 overflow-hidden">
      {/* Decorative Background Elements - Enhanced gradients */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-15%] right-[-20%] w-72 sm:w-96 h-72 sm:h-96 bg-gradient-to-br from-amber-200/40 to-rose-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-[-15%] left-[-20%] w-80 sm:w-[28rem] h-80 sm:h-[28rem] bg-gradient-to-tr from-brand-300/30 to-violet-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-brand-100/20 rounded-full blur-3xl" />
      </div>

      {/* Main Content - Dynamic spacing */}
      <div className="relative flex-1 flex flex-col justify-center items-center px-5 sm:px-6 py-8 sm:py-10 safe-top safe-bottom">
        <div className="w-full max-w-sm sm:max-w-md mx-auto">
          {/* Logo & Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="relative inline-block mb-5 sm:mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-400/40 to-amber-400/40 rounded-2xl sm:rounded-3xl blur-2xl scale-125" />
              <Logo className="relative w-18 h-18 sm:w-22 sm:h-22 md:w-24 md:h-24 shadow-2xl shadow-brand-300/30 rounded-2xl sm:rounded-3xl" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-stone-800 via-brand-700 to-stone-700 bg-clip-text text-transparent mb-2 sm:mb-3">
              CoExpect
            </h1>
            <p className="text-stone-600 text-base sm:text-lg md:text-xl font-medium mb-2">
              Your pregnancy, together.
            </p>
            <p className="text-[11px] sm:text-xs text-stone-400">
              No downloads • Works on any device • Free to start
            </p>
          </div>

          {/* Sign In Card - Compact with no layout shift */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-xl shadow-stone-200/50 border border-white/80 ring-1 ring-stone-100/50 p-4 sm:p-5">
            {/* Terms Checkbox - Improved touch target for iOS */}
            <div
              className={`flex items-start gap-3 p-3 min-h-12 rounded-xl border-2 transition-colors duration-200 cursor-pointer active:scale-[0.99] ${termsAccepted ? 'border-emerald-500 bg-emerald-50/60' : 'border-stone-200 hover:border-stone-300'
                }`}
              onClick={() => setTermsAccepted(!termsAccepted)}
              style={{ touchAction: 'manipulation' }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTermsAccepted(!termsAccepted); } }}
            >
              <div className="pt-0.5 shrink-0">
                <input
                  id="terms-checkbox"
                  name="terms-checkbox"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => { e.stopPropagation(); setTermsAccepted(e.target.checked); }}
                  className="sr-only peer"
                  aria-label="I accept the Terms of Service and Privacy Policy"
                  aria-describedby="terms-description"
                />
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors duration-200 ${termsAccepted
                  ? 'bg-emerald-500 border-emerald-500'
                  : 'border-stone-300 bg-white'
                  }`} aria-hidden="true">
                  {termsAccepted && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span
                id="terms-description"
                className="text-xs sm:text-sm text-stone-600 leading-relaxed select-none"
              >
                I'm 18+, this is <strong>informational only</strong>, and I agree to the{' '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowTermsModal(true); }}
                  className="text-xs sm:text-sm text-brand-600 underline hover:text-brand-700 font-normal py-2 -my-2 px-1 -mx-1"
                  style={{ touchAction: 'manipulation' }}
                >
                  Terms & Privacy Policy
                </button>.
              </span>
            </div>

            {/* Google Sign-In Area - Fixed height of 48px to prevent layout shift */}
            <div className="mt-3 h-12 flex items-center justify-center">
              {status === 'loading' ? (
                <div className="flex items-center justify-center gap-2 animate-fade-in">
                  <Loader2 className="animate-spin w-5 h-5 text-brand-600" />
                  <span className="text-stone-600 font-medium text-sm">Signing in...</span>
                </div>
              ) : termsAccepted ? (
                <div
                  key="google-btn-active"
                  id="google-signin-button"
                  className="flex justify-center"
                  style={{ width: 300, height: 48 }}
                />
              ) : (
                /* Prompt when terms not accepted */
                <div className="flex items-center justify-center gap-2 text-stone-400 animate-fade-in">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-xs font-medium">Accept terms to continue</span>
                </div>
              )}
            </div>

            {status === 'error' && errorMessage && (
              <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl text-center animate-fade-in">
                <p className="text-red-600 font-medium text-sm">{errorMessage}</p>
                <button onClick={() => setStatus('idle')} className="text-red-500 text-xs mt-1 hover:underline">
                  Try again
                </button>
              </div>
            )}
          </div>

          {/* About Description */}
          <div className="mt-4 sm:mt-5 text-center">
            <p className="text-xs sm:text-sm text-stone-500 leading-relaxed max-w-xs sm:max-w-sm mx-auto">
              Track your pregnancy with personalized daily guidance,
              powerful tools, and a supportive community—<span className="font-semibold text-stone-600">whether you're
                carrying or the partner</span>.
            </p>
          </div>

          {/* Feature Pills - Responsive sizing */}
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mt-4 sm:mt-5">
            {[
              { emoji: '🔗', label: 'Partner Sync' },
              { emoji: '🔔', label: 'Smart Reminders' },
              { emoji: '👶', label: 'Baby Names' },
              { emoji: '🍽️', label: 'Safe Foods' },
              { emoji: '📅', label: 'Countdown' },
            ].map(({ emoji, label }) => (
              <div
                key={label}
                className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/80 border border-stone-200/80 shadow-sm"
              >
                <span className="text-xs sm:text-sm">{emoji}</span>
                <span className="text-[10px] sm:text-xs font-medium text-stone-600">{label}</span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <p className="text-[10px] sm:text-xs text-stone-400 font-medium text-center mt-4 sm:mt-5 flex items-center justify-center gap-1">
            Made with <Heart size={10} className="text-brand-500 fill-brand-500" /> for growing families
          </p>
        </div>
      </div>

      {/* Legal Modal with Tabs - Mobile optimized */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShowTermsModal(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] flex flex-col shadow-2xl animate-slide-up sm:animate-scale-in sm:mx-4 safe-bottom" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-stone-100 shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600" />
                <h2 className="font-bold text-base sm:text-lg text-stone-900">Legal</h2>
              </div>
              <button
                onClick={() => setShowTermsModal(false)}
                className="p-1.5 sm:p-2 hover:bg-stone-100 rounded-full transition-colors"
              >
                <X size={18} className="text-stone-500 sm:w-5 sm:h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-stone-100 shrink-0">
              <button
                onClick={() => setLegalTab('terms')}
                className={`flex-1 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${legalTab === 'terms' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-stone-500 hover:text-stone-700'}`}
              >
                Terms of Service
              </button>
              <button
                onClick={() => setLegalTab('privacy')}
                className={`flex-1 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${legalTab === 'privacy' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-stone-500 hover:text-stone-700'}`}
              >
                Privacy Policy
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-stone-400 mb-3 sm:mb-4">Last Updated: {LEGAL_LAST_UPDATED}</p>
              <Suspense fallback={
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-brand-600 animate-spin" />
                </div>
              }>
                <LegalContent tab={legalTab} compact />
              </Suspense>
            </div>

            {/* Footer */}
            <div className="p-3 sm:p-4 border-t border-stone-100 shrink-0">
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-full py-2.5 sm:py-3 bg-stone-900 text-white rounded-xl font-bold text-sm sm:text-base hover:bg-stone-800 active:scale-[0.98] transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
